import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { pickErr, API_BASE } from "@/lib/api";

const ALL = "__all__";

function toAbsolute(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  // backend returns "/api/reports/<file>"; API_BASE already includes /api.
  const trimmed = url.replace(/^\/api/, "");
  return `${API_BASE}${trimmed}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(iso).slice(0, 10);
  }
}

export default function Reports() {
  const [searchParams] = useSearchParams();
  const preselectStudent = searchParams.get("student_id") || ALL;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [regenerating, setRegenerating] = useState({}); // {key: bool}

  // Filters
  const [classFilter, setClassFilter] = useState(ALL);
  const [assessmentFilter, setAssessmentFilter] = useState(ALL);
  const [studentFilter, setStudentFilter] = useState(preselectStudent);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/school/assignments");
      // Hide rows for archived (is_active=false) categories from the school
      // workflow — historical data is intact but never surfaced.
      setRows((Array.isArray(data) ? data : []).filter((r) => r.is_active));
    } catch (err) {
      setError(pickErr(err, "Failed to load reports"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Re-sync student filter if the URL changes (e.g. navigated from Students).
  useEffect(() => {
    setStudentFilter(preselectStudent);
  }, [preselectStudent]);

  // Filter option lists are derived from the loaded rows so they always match
  // the data the admin can actually see.
  const classOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.class_name).filter(Boolean));
    return Array.from(set).sort((a, b) => String(a).localeCompare(b));
  }, [rows]);

  const assessmentOptions = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      if (r.category_id && !map.has(r.category_id)) {
        map.set(r.category_id, r.category_name);
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [rows]);

  const studentOptions = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      if (!map.has(r.student_id)) {
        map.set(r.student_id, {
          id: r.student_id,
          name: r.student_name,
          class_name: r.class_name,
          student_code: r.student_code,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (classFilter !== ALL && r.class_name !== classFilter) return false;
      if (assessmentFilter !== ALL && r.category_id !== assessmentFilter)
        return false;
      if (studentFilter !== ALL && r.student_id !== studentFilter) return false;
      return true;
    });
  }, [rows, classFilter, assessmentFilter, studentFilter]);

  async function generateReport(row) {
    const key = `${row.student_id}_${row.category_id}`;
    setRegenerating((r) => ({ ...r, [key]: true }));
    setError("");
    try {
      const { data } = await api.post("/report", {
        student_id: row.student_id,
        category_id: row.category_id,
      });
      // Refresh just this row's report_url by reloading the listing.
      await load();
      window.open(toAbsolute(data.pdf_url), "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(pickErr(err, "Failed to generate report"));
    } finally {
      setRegenerating((r) => ({ ...r, [key]: false }));
    }
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 mb-1">Reports</h2>
      <p className="text-sm text-slate-500 mb-6">
        Filter and download student assessment reports.
      </p>

      {/* Filters — reuse the same dropdown styling we use elsewhere */}
      <section
        className="bg-white border border-slate-200 rounded-lg p-4 mb-4 grid sm:grid-cols-3 gap-3"
        data-testid="reports-filters"
      >
        <div>
          <label className="block text-xs text-slate-600 mb-1">Class</label>
          <select
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            data-testid="reports-filter-class"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
          >
            <option value={ALL}>All classes</option>
            {classOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-slate-600 mb-1">
            Assessment
          </label>
          <select
            value={assessmentFilter}
            onChange={(e) => setAssessmentFilter(e.target.value)}
            data-testid="reports-filter-assessment"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
          >
            <option value={ALL}>All assessments</option>
            {assessmentOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-slate-600 mb-1">Student</label>
          <select
            value={studentFilter}
            onChange={(e) => setStudentFilter(e.target.value)}
            data-testid="reports-filter-student"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
          >
            <option value={ALL}>All students</option>
            {studentOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.class_name})
              </option>
            ))}
          </select>
        </div>
      </section>

      {error ? (
        <p data-testid="reports-load-error" className="text-sm text-red-600 mb-3">
          {error}
        </p>
      ) : null}

      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between text-xs text-slate-500">
          <span data-testid="reports-row-count">
            {loading ? "Loading…" : `${filtered.length} row(s)`}
          </span>
          <button
            type="button"
            onClick={load}
            className="text-slate-600 hover:text-slate-900"
            data-testid="reports-refresh"
          >
            Refresh
          </button>
        </div>
        {loading ? (
          <p className="px-4 py-6 text-sm text-slate-500">Loading reports…</p>
        ) : filtered.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-500" data-testid="reports-empty">
            No reports match the selected filters.
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="reports-table">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-4 py-2 font-medium">Student</th>
                <th className="px-4 py-2 font-medium">Class</th>
                <th className="px-4 py-2 font-medium">Assessment</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Completed</th>
                <th className="px-4 py-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const key = `${r.student_id}_${r.category_id}`;
                const isCompleted = r.status === "completed";
                const busy = !!regenerating[key];
                return (
                  <tr
                    key={key}
                    className="border-t border-slate-100"
                    data-testid={`reports-row-${key}`}
                  >
                    <td className="px-4 py-2 text-slate-900">
                      {r.student_name}
                    </td>
                    <td className="px-4 py-2 text-slate-700">{r.class_name}</td>
                    <td className="px-4 py-2 text-slate-700">
                      {r.category_name}
                    </td>
                    <td className="px-4 py-2 text-slate-700 capitalize">
                      {r.status || "—"}
                    </td>
                    <td className="px-4 py-2 text-slate-700">
                      {formatDate(r.completed_at)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {!isCompleted ? (
                        <span className="text-xs text-slate-400">
                          Not available
                        </span>
                      ) : r.report_url ? (
                        <a
                          href={toAbsolute(r.report_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`reports-download-${key}`}
                          className="text-xs text-blue-700 hover:text-blue-900 underline"
                        >
                          Download
                        </a>
                      ) : (
                        <button
                          onClick={() => generateReport(r)}
                          disabled={busy}
                          data-testid={`reports-generate-${key}`}
                          className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                        >
                          {busy ? "Generating…" : "Generate"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
